import { useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import cogoToast from 'cogo-toast';
import { brandHealth } from '@shopmy/universal';

import './BrandTrackingHealthDetail.scss';
import OpportunityPerformanceChart from './OpportunityPerformanceChart';
import BrandTrackingHealthDetailData from './BrandTrackingHealthDetailData';
import BrandTrackingHealthVerdict from './BrandTrackingHealthVerdict';
import BrandTrackingHealthEmailHistory from './BrandTrackingHealthEmailHistory';
import { sendBrandTrackingHealthTestEmail } from '../../api/brand_tracking_health';
import { useBrandRecipientsModal } from '../BrandRecipientsModal/useBrandRecipientsModal';
import { ADMIN_SCOPES } from '../../constants/admin_scope_constants';
import { hasScope } from '../../helpers/scope_helpers';
import {
  ORDER_ATTRIBUTION_LINES,
  buildAllFamiliesChartModel,
  buildFamilyChartModel,
  buildBucketSeriesForRecords,
  getBrandHealthRecordsForScope,
  getTrackerHeadline,
  sumSeries
} from '../../helpers/brand_health_chart_helpers';

const { BRAND_HEALTH_INDICATOR_KEYS: IND, BRAND_HEALTH_EVENT_TYPES: EVENT } = brandHealth;

const OVERVIEW_GRANULARITY_OPTIONS = [
  { key: 'hourly', label: 'Hourly' },
  { key: 'daily', label: 'Daily' },
  { key: 'monthly', label: 'Monthly' }
];
const FAMILY_GRANULARITY_OPTIONS = [
  { key: 'daily', label: 'Daily' },
  { key: 'monthly', label: 'Monthly' }
];

const FAMILY_SECTIONS = [
  {
    name: 'Clicks & Confirmations',
    description: 'Link clicks arriving at ShopMy and the on-site pixel confirming they landed.',
    seenKey: IND.clickSuccess,
    seenLabel: 'Clicks',
    combinedSeenLabel: 'Clicks',
    trackedKey: IND.clickConfirmationSuccess,
    trackedLabel: 'Confirmations',
    combinedTrackedLabel: 'Click Confirmations',
    outcomeEventTypes: [EVENT.CLICK, EVENT.CLICK_CONFIRMATION]
  },
  {
    name: 'Orders',
    description: 'Order confirmations received and the ones that produced a ShopMy commission.',
    seenKey: IND.orderConfirmationSeen,
    seenLabel: 'Received',
    trackedKey: IND.orderConfirmationTracked,
    trackedLabel: 'Tracked',
    attributionLines: ORDER_ATTRIBUTION_LINES,
    outcomeEventTypes: [EVENT.ORDER_SEEN]
  },
  {
    name: 'Order Updates',
    description: 'Amount updates to existing orders.',
    seenKey: IND.orderUpdateSeen,
    seenLabel: 'Received',
    trackedKey: IND.orderUpdateSuccess,
    trackedLabel: 'Tracked',
    outcomeEventTypes: [EVENT.ORDER_UPDATE_SEEN]
  },
  {
    name: 'Cancellations',
    description: 'Order cancellations received and applied.',
    seenKey: IND.orderCancellationSeen,
    seenLabel: 'Received',
    trackedKey: IND.orderCancellationSuccess,
    trackedLabel: 'Tracked',
    outcomeEventTypes: [EVENT.ORDER_CANCEL_SEEN]
  }
];

const BrandTrackingHealthDetail = props => {
  const { Brand_id, brandName, onBack } = props;
  const [domainHealth, setDomainHealth] = useState(null);
  const [alertStates, setAlertStates] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [errorsSeenByDomain, setErrorsSeenByDomain] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [domain, setDomain] = useState(null);
  const [granularity, setGranularity] = useState('daily');
  const { openRecipients, recipientsModal } = useBrandRecipientsModal({
    renderActions: ({ Brand_id }) => <TrackingHealthTestEmailButton Brand_id={Brand_id} />
  });
  // The recipients routes gate on brand or user management (EDIT_BRAND also rides the catalog presets),
  // so the editor only shows to admins the save would let through.
  const currentUser = useSelector(state => state.users.currentUser);
  const canManageRecipients = hasScope(currentUser?.scopes || [], ADMIN_SCOPES.MANAGE_USERS, ADMIN_SCOPES.MANAGE_BRANDS);

  const domains = domainHealth ? Object.keys(domainHealth) : [];
  // A domain deep-linked from the list is derived from alert-state rows and may not be one of this
  // brand's registered domains — fall back to all-domains rather than rendering empty charts.
  const scopedDomain = domain && domains.includes(domain) ? domain : null;
  const records = domainHealth ? getBrandHealthRecordsForScope(domainHealth, scopedDomain) : [];
  const headline = domainHealth ? getTrackerHeadline(domainHealth, { domain: scopedDomain }) : null;
  const overviewModel = domainHealth ? buildAllFamiliesChartModel(records, FAMILY_SECTIONS, { granularity }) : null;
  const scopeLabel = scopedDomain || 'all domains';

  return (
    <div className='brand-tracking-health-detail'>
      <div className='brand-tracking-health-detail-scopebar'>
        <button type='button' className='brand-tracking-health-back' onClick={onBack}>
          ← All brands
        </button>
        <span className='brand-tracking-health-detail-brand'>{brandName}</span>
        <span className='brand-tracking-health-detail-label'>Viewing</span>
        {domains.length > 1 ? (
          <select className='brand-tracking-health-domain-select' value={scopedDomain || ''} onChange={e => setDomain(e.target.value || null)}>
            <option value=''>All domains</option>
            {domains.map(domainName => (
              <option key={domainName} value={domainName}>
                {domainName}
              </option>
            ))}
          </select>
        ) : (
          <span className='brand-tracking-health-detail-domain'>{domains[0] || '—'}</span>
        )}
        {canManageRecipients && (
          <button type='button' className='brand-tracking-health-manage-recipients' onClick={() => openRecipients({ Brand_id })}>
            Manage recipients
          </button>
        )}
        <div className='brand-tracking-health-granularity-tabs'>
          {OVERVIEW_GRANULARITY_OPTIONS.map(option => (
            <button
              key={option.key}
              type='button'
              className={cn('brand-tracking-health-granularity-tab', { 'is-active': granularity === option.key })}
              onClick={() => setGranularity(option.key)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className='brand-tracking-health-detail-skeleton' />
      ) : (
        <>
          <BrandTrackingHealthVerdict alertStates={alertStates} errorsByDomain={errorsSeenByDomain} onSelectDomain={setDomain} />

          <div className='brand-tracking-health-overview-card'>
            <div className='brand-tracking-health-overview-headline'>
              <span className='brand-tracking-health-overview-count'>{(headline?.eventsToday || 0).toLocaleString()}</span>
              <span className='brand-tracking-health-overview-caption'>
                events today across {scopeLabel}
                {headline?.windowErrorTotal ? ` · ${headline.windowErrorTotal.toLocaleString()} errors this window` : ''}
                {headline?.lastEventAt ? ` · last event ${moment(headline.lastEventAt).fromNow()}` : ''}
              </span>
            </div>
            <div className='brand-tracking-health-chart-wrapper'>
              <OpportunityPerformanceChart model={overviewModel} />
            </div>
          </div>

          <h2 className='brand-tracking-health-details-title'>
            Details by event type
            <span className='brand-tracking-health-details-scope'>{scopeLabel}</span>
          </h2>
          <div className='brand-tracking-health-family-sections'>
            {FAMILY_SECTIONS.map(family => (
              <BrandTrackingHealthFamilyCard key={family.name} records={records} family={family} />
            ))}
          </div>

          <BrandTrackingHealthEmailHistory emails={emailHistory} />
        </>
      )}

      {recipientsModal}

      <BrandTrackingHealthDetailData
        Brand_id={Brand_id}
        setDomainHealth={setDomainHealth}
        setAlertStates={setAlertStates}
        setEmailHistory={setEmailHistory}
        setErrorsSeenByDomain={setErrorsSeenByDomain}
        setIsLoading={setIsLoading}
      />
    </div>
  );
};

BrandTrackingHealthDetail.propTypes = {
  Brand_id: PropTypes.number.isRequired,
  brandName: PropTypes.string,
  onBack: PropTypes.func.isRequired
};

const BrandTrackingHealthFamilyCard = props => {
  const { records, family } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [granularity, setGranularity] = useState('daily');

  const receivedLast31Days = sumSeries(buildBucketSeriesForRecords(records, 'daily', family.seenKey));
  const chartModel = buildFamilyChartModel(records, family, { granularity });
  const windowTag = granularity === 'monthly' ? '(12mo)' : '(31d)';
  const receivedTotal = sumSeries(buildBucketSeriesForRecords(records, granularity, family.seenKey));
  const trackedTotal = family.trackedKey ? sumSeries(buildBucketSeriesForRecords(records, granularity, family.trackedKey)) : null;

  return (
    <div className={cn('brand-tracking-health-family-section', { 'is-open': isOpen })}>
      <button type='button' className='brand-tracking-health-family-header' onClick={() => setIsOpen(!isOpen)}>
        <div className='brand-tracking-health-family-meta'>
          <span className='brand-tracking-health-family-name'>{family.name}</span>
          <span className='brand-tracking-health-family-count'>{receivedLast31Days.toLocaleString()} received in the last 31 days</span>
        </div>
        <span className='brand-tracking-health-family-chevron'>▾</span>
      </button>
      {isOpen && (
        <div className='brand-tracking-health-chart-card'>
          <div className='brand-tracking-health-chart-card-top'>
            <div className='brand-tracking-health-chart-card-header'>
              <span className='brand-tracking-health-chart-card-title'>{family.name}</span>
              <span className='brand-tracking-health-chart-card-description'>{family.description}</span>
            </div>
            <div className='brand-tracking-health-granularity-tabs'>
              {FAMILY_GRANULARITY_OPTIONS.map(option => (
                <button
                  key={option.key}
                  type='button'
                  className={cn('brand-tracking-health-granularity-tab', { 'is-active': granularity === option.key })}
                  onClick={() => setGranularity(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className='brand-tracking-health-chart-wrapper'>
            <OpportunityPerformanceChart model={chartModel} />
          </div>
          <div className='brand-tracking-health-totals-row'>
            <span className='brand-tracking-health-total'>
              <span className='brand-tracking-health-swatch received' />
              {family.seenLabel} {windowTag}: {receivedTotal.toLocaleString()}
            </span>
            {family.trackedKey && (
              <span className='brand-tracking-health-total'>
                <span className='brand-tracking-health-swatch tracked' />
                {family.trackedLabel} {windowTag}: {trackedTotal.toLocaleString()}
              </span>
            )}
            {(family.attributionLines || []).map(attribution => (
              <span key={attribution.key} className='brand-tracking-health-total'>
                <span className={`brand-tracking-health-swatch ${attribution.tone}`} />
                {attribution.label} {windowTag}: {sumSeries(buildBucketSeriesForRecords(records, granularity, attribution.key)).toLocaleString()}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

BrandTrackingHealthFamilyCard.propTypes = {
  records: PropTypes.array.isRequired,
  family: PropTypes.object.isRequired
};

const TrackingHealthTestEmailButton = props => {
  const { Brand_id } = props;
  const [isSendingTest, setIsSendingTest] = useState(false);

  const sendTest = async () => {
    if (isSendingTest) return;
    setIsSendingTest(true);
    try {
      const { sentTo } = await sendBrandTrackingHealthTestEmail(Brand_id);
      cogoToast.success(`Test email sent to ${sentTo}.`);
    } catch (error) {
      cogoToast.error(error || 'Could not send the test email.');
    }
    setIsSendingTest(false);
  };

  return (
    <button type='button' onClick={sendTest} disabled={isSendingTest}>
      {isSendingTest ? 'Sending…' : 'Send me a test email'}
    </button>
  );
};

TrackingHealthTestEmailButton.propTypes = {
  Brand_id: PropTypes.number.isRequired
};

export default BrandTrackingHealthDetail;
